import { useState, useEffect, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "./msalConfig";
import axios from "axios";

axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';
axios.defaults.withCredentials = true;

// Bild (z.B. Screenshot) verkleinern und als komprimierte JPEG-data-URL zurückgeben,
// damit die Anhänge klein bleiben (längste Kante max. 1600px, Qualität 0.8).
function fileToCompressedDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Bild konnte nicht geladen werden"));
      img.onload = () => {
        const MAX = 1600;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const scale = Math.min(MAX / width, MAX / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const translations = {
  de: {
    loading: "Lade Anfragen...",
    title: "Admin-Übersicht",
    statTotal: "Gesamt",
    statDraft: "Entwürfe",
    statPending: "Ausstehend",
    statInProgress: "In Bearbeitung",
    statConfirmed: "Bestätigt",
    statCancelled: "Storniert",
    refresh: "🔄 Aktualisieren",
    newRequests: "⬆ {count} neue Anfragen",
    noRequests: "Noch keine Anfragen vorhanden",
    createdAt: "Erstellt:",
    selectPrompt: "← Wählen Sie eine Anfrage aus der Liste aus",
    requestTitle: "Anfrage #",
    statusPending: "⏳ Ausstehend",
    statusInProgress: "🔧 In Bearbeitung",
    statusConfirmed: "✅ Bestätigt",
    statusCancelled: "❌ Storniert",
    statusDraft: "📝 Entwurf",
    draftAwaitingEmployee: "wartet auf Mitarbeiter",
    deleteBtn: "Löschen",
    confirmDelete: "Diese Anfrage wirklich löschen?",
    errorUpdate: "Fehler beim Aktualisieren des Status",
    errorDelete: "Fehler beim Löschen der Anfrage",
    userInfo: "👤 Benutzer-Informationen",
    name: "Name:",
    email: "Email:",
    plannedBy: "Geplant von:",
    groupRecipients: "Gruppen-Empfänger:",
    groupRequestTitle: "Gruppenanfrage",
    travelPeriod: "Reisezeitraum:",
    tripEndOpen: "offen",
    hotelPeriod: "Hotel:",
    onSitePeriod: "Vor Ort:",
    businessTripNumber: "Dienstreisenummer:",
    projectNumber: "Projektnummer:",
    projectNumberEdit: "Bearbeiten",
    projectNumberSave: "Speichern",
    projectNumberCancel: "Abbrechen",
    projectNumberNone: "—",
    adminInfoTitle: "Infos / Reservierungen:",
    adminInfoEdit: "Bearbeiten",
    adminInfoSave: "Speichern",
    adminInfoCancel: "Abbrechen",
    adminInfoEmpty: "— noch keine Infos —",
    adminInfoPlaceholder: "Reservierungslinks, Buchungsnummern, Hinweise …",
    periodTo: "bis",
    created: "Erstellt:",
    travelPlan: "✅ Gebuchte Reiseplanung",
    flights: "✈️ Flüge",
    route: "Strecke:",
    date: "Datum:",
    airlines: "Airlines:",
    price: "Preis:",
    luggage: "Gepäck:",
    openFlights: "🔗 Google Flights öffnen →",
    hotelsTitle: "🏨 Hotels",
    hotelsOptions: "Optionen",
    stars: "Sterne",
    rating: "Bewertung:",
    openBooking: "🔗 Booking.com öffnen →",
    carRental: "🚗 Mietwagen",
    carRentalNeeded: "✅ Mietwagen wird benötigt",
    userComment: "📝 Benutzer-Kommentar",
    conversation: "💬 Gesprächsverlauf",
    userLabel: "👤 Benutzer:",
    aiLabel: "🤖 KI:",
    language: "Sprache",
    messagesTitle: "💬 Nachrichten",
    noMessages: "Noch keine Nachrichten.",
    msgPlaceholder: "Antwort an Benutzer...",
    msgAdmin: "Admin",
    msgUser: "Benutzer",
    attachImage: "Bild anhängen (oder Screenshot einfügen)",
    removeImage: "Bild entfernen",
    imgError: "Bild konnte nicht verarbeitet werden.",
    close: "Schließen",
    send: "Senden",
    tabRequests: "📋 Anfragen",
    tabUsers: "👥 Benutzer",
    tabAccount: "👤 Konto",
    accountTitle: "Konto",
    logout: "Abmelden",
    usersTitle: "👥 Benutzerverwaltung",
    noUsers: "Keine Benutzer gefunden",
    colName: "Name",
    colEmail: "E-Mail",
    colRequests: "Anfragen",
    colRole: "Rolle",
    roleAdmin: "Admin",
    roleUser: "Benutzer",
    rolePlanner: "Projektmanager",
    roleMakeAdmin: "Zu Admin machen",
    roleMakeUser: "Zu Benutzer machen",
    roleConfirm: (name, role) => `Rolle von "${name}" auf "${role}" setzen?`,
    roleError: "Fehler beim Ändern der Rolle",
    deleteUser: "Löschen",
    deleteUserConfirm: (name) => `Benutzer "${name}" und alle zugehörigen Daten wirklich löschen?`,
    deleteUserError: "Fehler beim Löschen des Benutzers",
    notifyTitle: "E-Mail-Benachrichtigungen",
    notifyLabel: "Benachrichtigung bei neuer Anfrage",
    notifyOn: "Aktiviert",
    notifyOff: "Deaktiviert",
    followUpOf: "Folgeantrag zu:",
  },
  en: {
    loading: "Loading requests...",
    title: "Admin Overview",
    statTotal: "Total",
    statDraft: "Drafts",
    statPending: "Pending",
    statInProgress: "In Progress",
    statConfirmed: "Confirmed",
    statCancelled: "Cancelled",
    refresh: "🔄 Refresh",
    newRequests: "⬆ {count} new requests",
    noRequests: "No requests yet",
    createdAt: "Created:",
    selectPrompt: "← Select a request from the list",
    requestTitle: "Request #",
    statusPending: "⏳ Pending",
    statusInProgress: "🔧 In Progress",
    statusConfirmed: "✅ Confirmed",
    statusCancelled: "❌ Cancelled",
    statusDraft: "📝 Draft",
    draftAwaitingEmployee: "awaiting employee",
    deleteBtn: "Delete",
    confirmDelete: "Really delete this request?",
    errorUpdate: "Error updating status",
    errorDelete: "Error deleting request",
    userInfo: "👤 User Information",
    name: "Name:",
    email: "Email:",
    plannedBy: "Planned by:",
    groupRecipients: "Group recipients:",
    groupRequestTitle: "Group request",
    travelPeriod: "Travel period:",
    tripEndOpen: "open",
    hotelPeriod: "Hotel:",
    onSitePeriod: "On-site:",
    businessTripNumber: "Business Trip Number:",
    projectNumber: "Project Number:",
    projectNumberEdit: "Edit",
    projectNumberSave: "Save",
    projectNumberCancel: "Cancel",
    projectNumberNone: "—",
    adminInfoTitle: "Info / Reservations:",
    adminInfoEdit: "Edit",
    adminInfoSave: "Save",
    adminInfoCancel: "Cancel",
    adminInfoEmpty: "— no info yet —",
    adminInfoPlaceholder: "Reservation links, booking numbers, notes …",
    periodTo: "to",
    created: "Created:",
    travelPlan: "✅ Booked Travel Plan",
    flights: "✈️ Flights",
    route: "Route:",
    date: "Date:",
    airlines: "Airlines:",
    price: "Price:",
    luggage: "Luggage:",
    openFlights: "🔗 Open Google Flights →",
    hotelsTitle: "🏨 Hotels",
    hotelsOptions: "Options",
    stars: "Stars",
    rating: "Rating:",
    openBooking: "🔗 Open Booking.com →",
    carRental: "🚗 Car Rental",
    carRentalNeeded: "✅ Car rental required",
    userComment: "📝 User Comment",
    conversation: "💬 Conversation History",
    userLabel: "👤 User:",
    aiLabel: "🤖 AI:",
    language: "Language",
    messagesTitle: "💬 Messages",
    noMessages: "No messages yet.",
    msgPlaceholder: "Reply to user...",
    msgAdmin: "Admin",
    msgUser: "User",
    attachImage: "Attach image (or paste a screenshot)",
    removeImage: "Remove image",
    imgError: "Image could not be processed.",
    close: "Close",
    send: "Send",
    tabRequests: "📋 Requests",
    tabUsers: "👥 Users",
    tabAccount: "👤 Account",
    accountTitle: "Account",
    logout: "Log out",
    usersTitle: "👥 User Management",
    noUsers: "No users found",
    colName: "Name",
    colEmail: "Email",
    colRequests: "Requests",
    colRole: "Role",
    roleAdmin: "Admin",
    roleUser: "User",
    rolePlanner: "Project Manager",
    roleMakeAdmin: "Make Admin",
    roleMakeUser: "Make User",
    roleConfirm: (name, role) => `Set role of "${name}" to "${role}"?`,
    roleError: "Error changing role",
    deleteUser: "Delete",
    deleteUserConfirm: (name) => `Really delete user "${name}" and all their data?`,
    deleteUserError: "Error deleting user",
    notifyTitle: "Email Notifications",
    notifyLabel: "Notify me on new request",
    notifyOn: "Enabled",
    notifyOff: "Disabled",
    followUpOf: "Follow-up to:",
  },
  pl: {
    loading: "Ładowanie zapytań...",
    title: "Panel Administracyjny",
    statTotal: "Łącznie",
    statDraft: "Szkice",
    statPending: "Oczekujące",
    statInProgress: "W trakcie",
    statConfirmed: "Potwierdzone",
    statCancelled: "Anulowane",
    refresh: "🔄 Odśwież",
    newRequests: "⬆ {count} nowych zapytań",
    noRequests: "Brak zapytań",
    createdAt: "Utworzono:",
    selectPrompt: "← Wybierz zapytanie z listy",
    requestTitle: "Zapytanie #",
    statusPending: "⏳ Oczekujące",
    statusInProgress: "🔧 W trakcie",
    statusConfirmed: "✅ Potwierdzone",
    statusCancelled: "❌ Anulowane",
    statusDraft: "📝 Szkic",
    draftAwaitingEmployee: "oczekuje na pracownika",
    deleteBtn: "Usuń",
    confirmDelete: "Czy naprawdę usunąć to zapytanie?",
    errorUpdate: "Błąd podczas aktualizacji statusu",
    errorDelete: "Błąd podczas usuwania zapytania",
    userInfo: "👤 Informacje o użytkowniku",
    name: "Imię:",
    email: "Email:",
    plannedBy: "Zaplanowane przez:",
    groupRecipients: "Odbiorcy grupy:",
    groupRequestTitle: "Zapytanie grupowe",
    travelPeriod: "Okres podróży:",
    tripEndOpen: "otwarty",
    hotelPeriod: "Hotel:",
    onSitePeriod: "Na miejscu:",
    businessTripNumber: "Numer podróży służbowej:",
    projectNumber: "Numer projektu:",
    projectNumberEdit: "Edytuj",
    projectNumberSave: "Zapisz",
    projectNumberCancel: "Anuluj",
    projectNumberNone: "—",
    adminInfoTitle: "Informacje / Rezerwacje:",
    adminInfoEdit: "Edytuj",
    adminInfoSave: "Zapisz",
    adminInfoCancel: "Anuluj",
    adminInfoEmpty: "— brak informacji —",
    adminInfoPlaceholder: "Linki rezerwacyjne, numery rezerwacji, uwagi …",
    periodTo: "do",
    created: "Utworzono:",
    travelPlan: "✅ Zaplanowana Podróż",
    flights: "✈️ Loty",
    route: "Trasa:",
    date: "Data:",
    airlines: "Linie lotnicze:",
    price: "Cena:",
    luggage: "Bagaż:",
    openFlights: "🔗 Otwórz Google Flights →",
    hotelsTitle: "🏨 Hotele",
    hotelsOptions: "Opcji",
    stars: "Gwiazdki",
    rating: "Ocena:",
    openBooking: "🔗 Otwórz Booking.com →",
    carRental: "🚗 Wynajem samochodu",
    carRentalNeeded: "✅ Wynajem samochodu wymagany",
    userComment: "📝 Komentarz użytkownika",
    conversation: "💬 Historia rozmowy",
    userLabel: "👤 Użytkownik:",
    aiLabel: "🤖 AI:",
    language: "Język",
    messagesTitle: "💬 Wiadomości",
    noMessages: "Brak wiadomości.",
    msgPlaceholder: "Odpowiedź do użytkownika...",
    msgAdmin: "Admin",
    msgUser: "Użytkownik",
    attachImage: "Dołącz obraz (lub wklej zrzut ekranu)",
    removeImage: "Usuń obraz",
    imgError: "Nie udało się przetworzyć obrazu.",
    close: "Zamknij",
    send: "Wyślij",
    tabRequests: "📋 Zapytania",
    tabUsers: "👥 Użytkownicy",
    tabAccount: "👤 Konto",
    accountTitle: "Konto",
    logout: "Wyloguj",
    usersTitle: "👥 Zarządzanie użytkownikami",
    noUsers: "Brak użytkowników",
    colName: "Imię",
    colEmail: "E-mail",
    colRequests: "Zapytania",
    colRole: "Rola",
    roleAdmin: "Admin",
    roleUser: "Użytkownik",
    rolePlanner: "Kierownik projektu",
    roleMakeAdmin: "Ustaw jako Admin",
    roleMakeUser: "Ustaw jako Użytkownik",
    roleConfirm: (name, role) => `Ustawić rolę "${name}" na "${role}"?`,
    roleError: "Błąd podczas zmiany roli",
    deleteUser: "Usuń",
    deleteUserConfirm: (name) => `Naprawdę usunąć użytkownika "${name}" i wszystkie jego dane?`,
    deleteUserError: "Błąd podczas usuwania użytkownika",
    notifyTitle: "Powiadomienia e-mail",
    notifyLabel: "Powiadamiaj o nowym zapytaniu",
    notifyOn: "Włączone",
    notifyOff: "Wyłączone",
    followUpOf: "Kontynuacja:",
  },
};

// Deep-Link aus einer Benachrichtigungs-Mail (?request=<id>&open=messages) einmalig auslesen.
// Der Wert wandert in den sessionStorage, weil der MSAL-Login-Redirect die URL austauscht.
function readDeepLink() {
  try {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("request");
    if (id) {
      sessionStorage.setItem("deepLinkRequest", JSON.stringify({ id, open: params.get("open") || "" }));
      params.delete("request");
      params.delete("open");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    const stored = sessionStorage.getItem("deepLinkRequest");
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function AdminPanel() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "de");
  const t = translations[lang] || translations.de;

  const toggleLang = (e) => {
    const next = e.target.value;
    localStorage.setItem("lang", next);
    setLang(next);
  };

  // ── Auth state ──
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("adminToken") || "");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Hilfsfunktion: Auth-Header für alle Admin-API-Aufrufe
  const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token || adminToken}` }, withCredentials: true });

  const [activeTab, setActiveTab] = useState("requests");
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);

  // ── Data state ──
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState("");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [adminMessages, setAdminMessages] = useState([]);
  const [adminMsgInput, setAdminMsgInput] = useState("");
  const [adminMsgImages, setAdminMsgImages] = useState([]); // ausstehende Bild-Anhänge (data-URLs) vor dem Senden
  const [adminImgBusy, setAdminImgBusy] = useState(false); // während Bilder komprimiert werden
  const [lightboxSrc, setLightboxSrc] = useState(null); // Bild in einem In-App-Dialog groß anzeigen
  const [editingProject, setEditingProject] = useState(false); // Projektnummer der ausgewählten Anfrage bearbeiten
  const [projectNumberDraft, setProjectNumberDraft] = useState("");
  const [editingInfo, setEditingInfo] = useState(false); // Admin-Infos der ausgewählten Anfrage bearbeiten
  const [infoDraft, setInfoDraft] = useState("");
  const [siblings, setSiblings] = useState([]); // Gruppen-Empfänger einer geplanten Anfrage (Provenienz)
  const [expandedGroups, setExpandedGroups] = useState({}); // groupId -> aufgeklappt?
  const [newRequestCount, setNewRequestCount] = useState(0);
  const [newRequestIds, setNewRequestIds] = useState({});
  const requestListRef = useRef(null);
  const knownRequestIdsRef = useRef(new Set());
  const requestFeedInitializedRef = useRef(false);
  const [deepLink, setDeepLink] = useState(readDeepLink); // aus einer Benachrichtigungs-Mail geöffnete Anfrage

  // Restore session on mount (JWT-based)
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) { setAuthLoading(false); return; }
    // Validate existing JWT by calling a protected endpoint
    axios.get("/api/admin/users", { headers: { Authorization: `Bearer ${token}` }, withCredentials: true })
      .then(() => axios.get("/api/profile", { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }))
      .then((profileRes) => {
        setAdminUser(profileRes.data.user);
        setAdminToken(token);
        setIsLoggedIn(true);
      })
      .catch(() => { localStorage.removeItem("adminToken"); })
      .finally(() => setAuthLoading(false));
  }, []);

  // Handle MSAL redirect result — fires after loginRedirect() returns to this page
  useEffect(() => {
    if (
      isAuthenticated &&
      accounts.length > 0 &&
      inProgress === InteractionStatus.None &&
      !isLoggedIn &&
      !authLoading
    ) {
      setLoginLoading(true);
      setLoginError("");
      instance
        .acquireTokenSilent({ ...loginRequest, account: accounts[0] })
        .then(async (tokenResponse) => {
          try {
            const res = await axios.post("/api/auth/entra", { idToken: tokenResponse.idToken }, { withCredentials: true });
            if (res.data.user?.role !== "admin") {
              setLoginError("No admin access. Please sign in with an admin account.");
              setLoginLoading(false);
              return;
            }
            const token = res.data.adminToken || res.data.token || "";
            localStorage.setItem("adminToken", token);
            setAdminToken(token);
            setAdminUser(res.data.user);
            setIsLoggedIn(true);
          } catch (err) {
            setLoginError("Sign-in failed.");
          } finally {
            setLoginLoading(false);
          }
        })
        .catch(() => {
          setLoginError("Sign-in failed.");
          setLoginLoading(false);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, inProgress, authLoading]);

  // Auto-fetch requests once logged in
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchRequests();
    fetchUsers();
    axios.get("/api/admin/me/notify", authHeaders())
      .then((res) => setNotifyEnabled(res.data.notifyOnNewRequest ?? false))
      .catch(() => {});
    const interval = setInterval(fetchRequests, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const handleLogin = (forceAccountSelect = false) => {
    setLoginError("");
    instance.loginRedirect({
      ...loginRequest,
      ...(forceAccountSelect ? { prompt: "select_account" } : {}),
    });
  };

  const handleLogout = async () => {
    try {
      await axios.post("/api/logout", {}, { withCredentials: true });
    } catch (_) {}
    localStorage.removeItem("adminToken");
    setAdminToken("");
    setIsLoggedIn(false);
    setAdminUser(null);
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  const fetchUsers = async () => {
    setUsersError("");
    try {
      const res = await axios.get("/api/admin/users", authHeaders());
      setUsers(res.data);
    } catch (err) {
      console.error("Fehler beim Laden der Benutzer:", err);
      const msg = err.response?.data?.error || `HTTP ${err.response?.status}` || err.message;
      setUsersError(msg);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(t.deleteUserConfirm(user.name))) return;
    try {
      await axios.delete(`/api/admin/users/${user.id}`, authHeaders());
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (err) {
      alert(err.response?.data?.error || t.deleteUserError);
    }
  };

  const fetchRequests = async ({ manual = false } = {}) => {
    try {
      const res = await axios.get("/api/admin/requests", authHeaders());
      const nextIds = new Set((res.data || []).map(r => r.id));
      if (requestFeedInitializedRef.current) {
        let unseen = 0;
        const unseenIds = [];
        nextIds.forEach((id) => {
          if (!knownRequestIdsRef.current.has(id)) {
            unseen += 1;
            unseenIds.push(id);
          }
        });
        if (unseen > 0 && activeTab === "requests") {
          setNewRequestCount(prev => prev + unseen);
          setNewRequestIds(prev => {
            const next = { ...prev };
            unseenIds.forEach((id) => { next[id] = true; });
            return next;
          });
        }
      } else {
        requestFeedInitializedRef.current = true;
      }
      knownRequestIdsRef.current = nextIds;
      setRequests(res.data);
      if (manual) {
        setNewRequestCount(0);
        setNewRequestIds({});
      }
      setLoading(false);
    } catch (err) {
      console.error("Fehler beim Laden der Anfragen:", err);
      setLoading(false);
    }
  };

  const jumpToLatestRequests = () => {
    if (requestListRef.current) {
      requestListRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    setNewRequestCount(0);
  };

  const clearNewMarker = (reqId) => {
    if (!reqId) return;
    setNewRequestIds(prev => {
      if (!prev[reqId]) return prev;
      const next = { ...prev };
      delete next[reqId];
      return next;
    });
  };

  const saveProjectNumber = async (id) => {
    try {
      const value = projectNumberDraft.trim();
      await axios.patch(`/api/requests/${id}/project-number`, { projectNumber: value }, authHeaders());
      setRequests(prev => prev.map(r => r.id === id ? { ...r, projectNumber: value } : r));
      setSelectedRequest(prev => prev && prev.id === id ? { ...prev, projectNumber: value } : prev);
      setEditingProject(false);
    } catch (err) {
      console.error("Fehler beim Speichern der Projektnummer:", err);
      alert("Fehler: " + (err.response?.data?.error || err.message));
    }
  };

  const saveAdminInfo = async (id) => {
    try {
      const value = infoDraft;
      await axios.patch(`/api/admin/requests/${id}/info`, { adminInfo: value }, authHeaders());
      setRequests(prev => prev.map(r => r.id === id ? { ...r, adminInfo: value } : r));
      setSelectedRequest(prev => prev && prev.id === id ? { ...prev, adminInfo: value } : prev);
      setEditingInfo(false);
    } catch (err) {
      console.error("Fehler beim Speichern der Infos:", err);
      alert("Fehler: " + (err.response?.data?.error || err.message));
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const statusBy = newStatus !== "pending" ? (adminUser?.name || adminUser?.email || "Admin") : undefined;
      await axios.patch(`/api/admin/requests/${id}`, { status: newStatus, statusBy }, authHeaders());
      const res = await axios.get("/api/admin/requests", authHeaders());
      setRequests(res.data);
      if (selectedRequest?.id === id) {
        const updated = res.data.find(r => r.id === id);
        if (updated) setSelectedRequest(updated);
      }
    } catch (err) {
      console.error("Fehler beim Aktualisieren:", err);
      alert(t.errorUpdate);
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await axios.delete(`/api/admin/requests/${id}`, authHeaders());
      fetchRequests();
      if (selectedRequest?.id === id) setSelectedRequest(null);
    } catch (err) {
      console.error("Fehler beim Löschen:", err);
      alert(t.errorDelete);
    }
  };

  const fetchAdminMessages = async (reqId) => {
    try {
      const res = await axios.get(`/api/requests/${reqId}/messages`, authHeaders());
      setAdminMessages(res.data);
    } catch (err) {
      console.error("Fehler beim Laden der Nachrichten:", err);
    }
  };

  // Bild-Dateien (aus Dateiauswahl oder Zwischenablage) komprimieren und zu den Anhängen hinzufügen
  const addAdminImageFiles = async (files) => {
    const imageFiles = Array.from(files || []).filter(f => f.type.startsWith("image/"));
    if (imageFiles.length === 0) return;
    setAdminImgBusy(true);
    try {
      const dataUrls = await Promise.all(imageFiles.map(fileToCompressedDataUrl));
      setAdminMsgImages(prev => [...prev, ...dataUrls].slice(0, 10));
    } catch (err) {
      console.error("Bild konnte nicht verarbeitet werden:", err);
      alert(t.imgError);
    } finally {
      setAdminImgBusy(false);
    }
  };

  const handleAdminPaste = (e) => {
    const files = Array.from(e.clipboardData?.items || [])
      .filter(it => it.type.startsWith("image/"))
      .map(it => it.getAsFile())
      .filter(Boolean);
    if (files.length) {
      e.preventDefault();
      addAdminImageFiles(files);
    }
  };

  const sendAdminMessage = async () => {
    if ((!adminMsgInput.trim() && adminMsgImages.length === 0) || !selectedRequest) return;
    try {
      await axios.post(`/api/requests/${selectedRequest.id}/messages`, {
        text: adminMsgInput.trim(),
        images: adminMsgImages,
        sender: "admin"
      }, authHeaders());
      setAdminMsgInput("");
      setAdminMsgImages([]);
      fetchAdminMessages(selectedRequest.id);
    } catch (err) {
      console.error("Fehler beim Senden:", err);
      alert("Message could not be sent: " + (err.response?.data?.error || err.message));
    }
  };

  useEffect(() => {
    setEditingProject(false);
    if (!selectedRequest) { setAdminMessages([]); setSiblings([]); return; }
    setAdminMsgInput("");
    setAdminMsgImages([]);
    fetchAdminMessages(selectedRequest.id);
    // Gruppen-Empfänger laden, falls die Anfrage von einem Planer stammt
    if (selectedRequest.groupId) {
      axios.get(`/api/admin/requests/${selectedRequest.id}/siblings`, authHeaders())
        .then((res) => setSiblings(res.data || []))
        .catch(() => setSiblings([]));
    } else {
      setSiblings([]);
    }
    const interval = setInterval(() => fetchAdminMessages(selectedRequest.id), 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRequest]);

  // Deep-Link aus der Mail anwenden: Anfrage auswählen und zu den Nachrichten scrollen
  useEffect(() => {
    if (!deepLink || !isLoggedIn || loading) return;
    const req = requests.find(r => r.id === deepLink.id);
    if (req) {
      setActiveTab("requests");
      setActiveFilter(null); // sonst ist die Anfrage in der linken Liste evtl. ausgefiltert
      setSelectedRequest(req);
      if (deepLink.open === "messages") {
        requestAnimationFrame(() => {
          document.getElementById("admin-messages")?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    }
    // Auch bei gelöschter/unbekannter Anfrage verwerfen, sonst greift der Link bei jedem Render erneut
    sessionStorage.removeItem("deepLinkRequest");
    setDeepLink(null);
  }, [deepLink, isLoggedIn, loading, requests]);

  // Lightbox mit Escape-Taste schließen
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = e => { if (e.key === "Escape") setLightboxSrc(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxSrc]);

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "#ff9800";
      case "in progress": return "#9c27b0";
      case "confirmed": return "#4caf50";
      case "cancelled": return "#f44336";
      case "draft": return "#607d8b";
      default: return "#757575";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending": return t.statusPending;
      case "in progress": return t.statusInProgress;
      case "confirmed": return t.statusConfirmed;
      case "cancelled": return t.statusCancelled;
      case "draft": return t.statusDraft;
      default: return status;
    }
  };

  const getStatusStats = () => ({
    total: requests.length,
    draft: requests.filter(r => r.status === "draft").length,
    pending: requests.filter(r => r.status === "pending").length,
    inProgress: requests.filter(r => r.status === "in progress").length,
    confirmed: requests.filter(r => r.status === "confirmed").length,
    cancelled: requests.filter(r => r.status === "cancelled").length,
  });

  // ── Loading / Auth guards ──
  if (authLoading) return null;

  if (!isLoggedIn) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", backgroundColor: "#f0f4f8" }}>
        <div style={{ backgroundColor: "#fff", padding: 40, borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", width: "100%", maxWidth: 400 }}>
          <img src="/ZW_white.png" alt="Zwtrip Logo" style={{ height: 48, display: "block", marginBottom: 8 }} />
          <p style={{ color: "#666", marginBottom: 28, marginTop: 0 }}>Admin area</p>
          {loginError && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ padding: "10px 12px", backgroundColor: "#ffebee", color: "#c62828", borderRadius: 6, fontSize: 14, marginBottom: 10 }}>
                {loginError}
              </div>
              <button
                onClick={() => handleLogin(true)}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  backgroundColor: "#f5f5f5",
                  color: "#333",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  fontSize: 14,
                  cursor: "pointer",
                  fontFamily: "'Segoe UI', sans-serif",
                }}
              >
                Use a different account
              </button>
            </div>
          )}
          <button
            onClick={() => handleLogin()}
            disabled={loginLoading}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              padding: "12px 16px",
              backgroundColor: loginLoading ? "#e3e3e3" : "#fff",
              color: loginLoading ? "#999" : "#333",
              border: "1px solid #8c8c8c",
              borderRadius: 4,
              fontSize: 15,
              fontWeight: "600",
              cursor: loginLoading ? "not-allowed" : "pointer",
              boxShadow: loginLoading ? "none" : "0 1px 3px rgba(0,0,0,0.15)",
              fontFamily: "'Segoe UI', sans-serif",
            }}
          >
            {!loginLoading && (
              <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
            )}
            {loginLoading ? "Signing in..." : "Sign in with Microsoft"}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", fontSize: 18 }}>
        {t.loading}
      </div>
    );
  }

  const stats = getStatusStats();

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "Arial, sans-serif" }}>

      {/* ── Sidebar ── */}
      <div style={{ width: 220, backgroundColor: "#1a237e", color: "#fff", display: "flex", flexDirection: "column", padding: "24px 0", flexShrink: 0 }}>
        <div style={{ padding: "0 20px 24px", borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
          <img src="/ZW_white.png" alt="Zwtrip Logo" style={{ height: 40, display: "block", marginBottom: 6 }} />
          <div style={{ fontSize: 12, opacity: 0.6, letterSpacing: 1 }}>ADMIN</div>
          <div style={{ fontSize: 13, marginTop: 6, opacity: 0.8 }}>{adminUser?.name}</div>
        </div>

        <nav style={{ marginTop: 16, flex: 1, padding: "0 8px" }}>
          {[
            { key: "requests", label: t.tabRequests },
            { key: "users", label: t.tabUsers },
            { key: "account", label: t.tabAccount },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 14px", background: activeTab === tab.key ? "rgba(255,255,255,0.15)" : "transparent", color: "#fff", border: "none", borderLeft: activeTab === tab.key ? "4px solid #90caf9" : "4px solid transparent", fontSize: 14, fontWeight: activeTab === tab.key ? "bold" : "normal", cursor: "pointer", borderRadius: "0 6px 6px 0" }}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <div style={{ padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,0.15)" }}>
        </div>
      </div>

      {/* ── Main area ── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", flexDirection: "column" }}>

        {/* ══ USERS TAB ══ */}
        {activeTab === "users" && (
          <div style={{ flex: 1, overflowY: "auto", padding: 28, backgroundColor: "#f5f7fa" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ margin: 0 }}>{t.usersTitle}</h2>
              <button
                onClick={fetchUsers}
                style={{ padding: "8px 16px", backgroundColor: "#2196f3", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: "bold", cursor: "pointer" }}
              >
                {t.refresh}
              </button>
            </div>
            {usersError ? (
              <div style={{ padding: 20, backgroundColor: "#fff3f3", border: "1px solid #f44336", borderRadius: 8, color: "#c62828" }}>
                ⚠️ Fehler: {usersError}
              </div>
            ) : users.length === 0 ? (
              <div style={{ padding: 20, backgroundColor: "#fff", borderRadius: 8, textAlign: "center", color: "#999" }}>{t.noUsers}</div>
            ) : (
              <div style={{ backgroundColor: "#fff", borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#1a237e", color: "#fff" }}>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "bold", fontSize: 13 }}>{t.colName}</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "bold", fontSize: 13 }}>{t.colEmail}</th>
                      <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: "bold", fontSize: 13 }}>{t.colRole}</th>
                      <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: "bold", fontSize: 13 }}>{t.colRequests}</th>
                      <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: "bold", fontSize: 13 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={u.id} style={{ backgroundColor: i % 2 === 0 ? "#fafafa" : "#fff", borderBottom: "1px solid #eee" }}>
                        <td style={{ padding: "12px 16px", fontSize: 14 }}>{u.name}</td>
                        <td style={{ padding: "12px 16px", fontSize: 14, color: "#555" }}>{u.email}</td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <span style={{
                            display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: "bold", marginRight: 8,
                            backgroundColor: u.role === "admin" ? "#1a237e" : u.role === "planner" ? "#ff9800" : "#e0e0e0",
                            color: u.role === "admin" || u.role === "planner" ? "#fff" : "#555",
                          }}>
                            {u.role === "admin" ? t.roleAdmin : u.role === "planner" ? t.rolePlanner : t.roleUser}
                          </span>

                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <span style={{ display: "inline-block", minWidth: 28, padding: "2px 10px", backgroundColor: u.requestCount > 0 ? "#2196f3" : "#e0e0e0", color: u.requestCount > 0 ? "#fff" : "#999", borderRadius: 12, fontSize: 13, fontWeight: "bold" }}>
                            {u.requestCount}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          {u.id !== adminUser?.id && (
                            <button
                              onClick={() => handleDeleteUser(u)}
                              style={{ padding: "3px 10px", fontSize: 12, borderRadius: 5, border: "1px solid #f44336", backgroundColor: "#fff", cursor: "pointer", color: "#f44336" }}
                            >
                               {t.deleteUser}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ══ ACCOUNT TAB ══ */}
        {activeTab === "account" && (
          <div style={{ flex: 1, overflowY: "auto", padding: 28, backgroundColor: "#f5f7fa" }}>
            <div style={{ maxWidth: 480 }}>
              <h2 style={{ marginTop: 0 }}>{t.accountTitle}</h2>

              <div style={{ background: "#fff", borderRadius: 10, padding: 24, marginBottom: 20, boxShadow: "0 1px 6px rgba(0,0,0,0.08)" }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, color: "#888", marginBottom: 2 }}>Name</div>
                  <div style={{ fontSize: 16, fontWeight: "bold" }}>{adminUser?.name}</div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, color: "#888", marginBottom: 2 }}>Email</div>
                  <div style={{ fontSize: 16 }}>{adminUser?.email}</div>
                </div>
              </div>

              <div style={{ background: "#fff", borderRadius: 10, padding: 24, marginBottom: 20, boxShadow: "0 1px 6px rgba(0,0,0,0.08)" }}>
                <div style={{ fontSize: 13, color: "#888", marginBottom: 8 }}>{t.language}</div>
                <select
                  value={lang}
                  onChange={toggleLang}
                  style={{ padding: "8px 12px", border: "2px solid #1565c0", borderRadius: 6, fontSize: 14, color: "#1565c0", fontWeight: "bold", cursor: "pointer", backgroundColor: "#fff" }}
                >
                  <option value="de">🇩🇪 Deutsch</option>
                  <option value="en">🇬🇧 English</option>
                  <option value="pl">🇵🇱 Polski</option>
                </select>
              </div>

              <div style={{ background: "#fff", borderRadius: 10, padding: 24, marginBottom: 20, boxShadow: "0 1px 6px rgba(0,0,0,0.08)" }}>
                <div style={{ fontSize: 14, fontWeight: "bold", marginBottom: 16 }}>{t.notifyTitle}</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 14, color: "#333" }}>{t.notifyLabel}</span>
                  <button
                    disabled={notifyLoading}
                    onClick={async () => {
                      setNotifyLoading(true);
                      try {
                        const next = !notifyEnabled;
                        await axios.patch("/api/admin/me/notify", { notifyOnNewRequest: next }, authHeaders());
                        setNotifyEnabled(next);
                      } catch (err) {
                        alert(err.response?.data?.error || err.message);
                      } finally {
                        setNotifyLoading(false);
                      }
                    }}
                    style={{
                      padding: "6px 18px",
                      border: "none",
                      borderRadius: 20,
                      fontWeight: "bold",
                      fontSize: 13,
                      cursor: notifyLoading ? "not-allowed" : "pointer",
                      backgroundColor: notifyEnabled ? "#2e7d32" : "#e0e0e0",
                      color: notifyEnabled ? "#fff" : "#555",
                      minWidth: 90,
                      opacity: notifyLoading ? 0.6 : 1,
                    }}
                  >
                    {notifyEnabled ? t.notifyOn : t.notifyOff}
                  </button>
                </div>
              </div>

              <button
                onClick={handleLogout}
                style={{ padding: "10px 24px", backgroundColor: "#ef5350", color: "#fff", border: "none", borderRadius: 8, fontSize: 15, fontWeight: "bold", cursor: "pointer" }}
              >
                {t.logout}
              </button>
            </div>
          </div>
        )}

        {/* ══ REQUESTS TAB ══ */}
        {activeTab === "requests" && (
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* Left: request list */}
        <div
          ref={requestListRef}
          onScroll={() => {
            if (requestListRef.current && requestListRef.current.scrollTop < 24) {
              setNewRequestCount(0);
            }
          }}
          style={{ width: "40%", borderRight: "2px solid #ddd", padding: 20, overflowY: "auto", backgroundColor: "#f8f9fa" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h1 style={{ margin: 0, fontSize: 20 }}>{t.title}</h1>
          </div>

          {/* Stats */}
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            {[
              { key: null, count: stats.total, label: t.statTotal, color: "#2196f3", active: activeFilter === null },
              ...(stats.draft > 0 ? [{ key: "draft", count: stats.draft, label: t.statDraft, color: "#607d8b", active: activeFilter === "draft" }] : []),
              { key: "pending", count: stats.pending, label: t.statPending, color: "#ff9800", active: activeFilter === "pending" },
              { key: "in progress", count: stats.inProgress, label: t.statInProgress, color: "#9c27b0", active: activeFilter === "in progress" },
              { key: "confirmed", count: stats.confirmed, label: t.statConfirmed, color: "#4caf50", active: activeFilter === "confirmed" },
              { key: "cancelled", count: stats.cancelled, label: t.statCancelled, color: "#f44336", active: activeFilter === "cancelled" },
            ].map(({ key, count, label, color, active }) => (
              <div
                key={String(key)}
                onClick={() => setActiveFilter(f => f === key ? null : key)}
                style={{ flex: 1, minWidth: 72, padding: 10, backgroundColor: color, color: "white", borderRadius: 8, textAlign: "center", cursor: "pointer", boxShadow: active ? `0 0 0 3px ${color}88` : "none", opacity: activeFilter === null || active ? 1 : 0.7, transition: "all 0.15s" }}
              >
                <div style={{ fontSize: 22, fontWeight: "bold" }}>{count}</div>
                <div style={{ fontSize: 11 }}>{label}</div>
              </div>
            ))}
          </div>

          <button
            onClick={() => fetchRequests({ manual: true })}
            style={{ width: "100%", padding: 10, marginBottom: 15, backgroundColor: "#2196f3", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: "bold" }}
          >
            {t.refresh}
          </button>

          {newRequestCount > 0 && (
            <button
              onClick={jumpToLatestRequests}
              style={{ width: "100%", padding: "9px 10px", marginBottom: 14, backgroundColor: "#2e7d32", color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: "bold", boxShadow: "0 2px 8px rgba(46,125,50,0.3)" }}
            >
              {(t.newRequests || "⬆ {count} new requests").replace("{count}", String(newRequestCount))}
            </button>
          )}

          {requests.length === 0 ? (
            <div style={{ padding: 20, backgroundColor: "#fff", borderRadius: 8, textAlign: "center", color: "#999" }}>{t.noRequests}</div>
          ) : (() => {
            const filtered = requests.filter(req => activeFilter === null || req.status === activeFilter);
            // Planer-Sammelanfragen (gleiche groupId) nur solange es Entwürfe sind als graue Karte anzeigen.
            // Bereits abgesendete Entwürfe (z.B. pending) werden als Einzelanfrage geführt,
            // damit Folgeanträge korrekt an die Ursprungsanfrage angehängt werden können.
            const groupsMap = {};
            const singles = [];
            filtered.forEach(r => {
              if (r.groupId && r.status === "draft") (groupsMap[r.groupId] = groupsMap[r.groupId] || []).push(r);
              else singles.push(r);
            });
            const groupEntries = Object.entries(groupsMap)
              .map(([gid, members]) => ({ gid, members: members.slice().sort((a,b) => new Date(a.timestamp)-new Date(b.timestamp)) }))
              .sort((a,b) => new Date(b.members[0].timestamp) - new Date(a.members[0].timestamp));

            // Einzelanfragen mit Folgeantrag-Kette.
            // Ketten werden nach letzter Aktivität sortiert (neueste zuerst),
            // damit neue Anfragen/Folgeanträge sofort oben sichtbar sind.
            const byId = {};
            requests.forEach(r => { byId[r.id] = r; });
            const filteredIds = new Set(singles.map(r => r.id));
            const roots = singles.filter(r => !r.linkedToId || !byId[r.linkedToId]);
            const followUps = singles.filter(r => r.linkedToId && byId[r.linkedToId]);
            const childrenByParent = {};
            followUps.forEach((f) => {
              if (!childrenByParent[f.linkedToId]) childrenByParent[f.linkedToId] = [];
              childrenByParent[f.linkedToId].push(f);
            });
            Object.values(childrenByParent).forEach(arr => arr.sort((a,b) => new Date(a.timestamp)-new Date(b.timestamp)));
            const ordered = [];
            const latestByRoot = {};
            const getLatestTs = (id) => {
              if (latestByRoot[id] !== undefined) return latestByRoot[id];
              const selfTs = byId[id] ? new Date(byId[id].timestamp).getTime() : 0;
              const childTs = (childrenByParent[id] || []).reduce((maxTs, child) => {
                const ts = getLatestTs(child.id);
                return ts > maxTs ? ts : maxTs;
              }, 0);
              latestByRoot[id] = selfTs > childTs ? selfTs : childTs;
              return latestByRoot[id];
            };
            const insertChain = (req) => {
              ordered.push(req);
              (childrenByParent[req.id] || []).forEach(insertChain);
            };
            roots.slice().sort((a,b) => getLatestTs(b.id) - getLatestTs(a.id)).forEach(insertChain);
            singles.filter(r => !ordered.includes(r)).forEach(r => ordered.push(r));

            const shortStatus = (s) => getStatusLabel(s).replace(/^[^\s]+\s/, "");

            return (
            <>
            {/* Gruppenanfragen (Planer) */}
            {groupEntries.map(({ gid, members }) => {
              const plan = members[0].finalPlan?.requirements;
              const expanded = !!expandedGroups[gid];
              const anySelected = members.some(m => m.id === selectedRequest?.id);
              const hasNewMember = members.some(m => !!newRequestIds[m.id]);
              const tally = {};
              members.forEach(m => { tally[m.status] = (tally[m.status] || 0) + 1; });
              return (
                <div key={gid} style={{ marginBottom: 8, borderRadius: 8, border: hasNewMember ? "2px solid #43a047" : anySelected ? "2px solid #2196f3" : "1px solid #cfd8dc", overflow: "hidden", boxShadow: hasNewMember ? "0 0 0 3px rgba(67,160,71,0.15)" : "0 1px 3px rgba(0,0,0,0.08)", transition: "all 0.2s" }}>
                  <div
                    onClick={() => {
                      setExpandedGroups(p => ({ ...p, [gid]: !p[gid] }));
                      members.forEach((m) => clearNewMarker(m.id));
                    }}
                    style={{ padding: "9px 11px", cursor: "pointer", backgroundColor: "#eceff1" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: "bold", fontSize: 14 }}>👥 {t.groupRequestTitle} · {members.length}</div>
                        <div style={{ fontSize: 12, color: "#607d8b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {t.plannedBy} {members[0].plannedByName}
                        </div>
                      </div>
                      <span style={{ fontSize: 14, color: "#555", flexShrink: 0 }}>{expanded ? "▴" : "▾"}</span>
                    </div>
                    {plan?.from && plan?.to && (
                      <div style={{ fontSize: 12, color: "#777", marginTop: 2 }}>✈️ {plan.from} → {plan.to}</div>
                    )}
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
                      {Object.entries(tally).map(([st, ct]) => (
                        <span key={st} style={{ fontSize: 10, fontWeight: "bold", color: "#fff", backgroundColor: getStatusColor(st), borderRadius: 10, padding: "1px 7px" }}>
                          {shortStatus(st)} {ct}
                        </span>
                      ))}
                    </div>
                  </div>
                  {expanded && (
                    <div style={{ backgroundColor: "#fff" }}>
                      {members.map(m => (
                        <div
                          key={m.id}
                          onClick={() => {
                            setSelectedRequest(m);
                            clearNewMarker(m.id);
                          }}
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 11px", borderTop: "1px solid #eee", cursor: "pointer", backgroundColor: selectedRequest?.id === m.id ? "#e3f2fd" : "#fff" }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: "bold", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.userName}</div>
                            {m.userEmail && <div style={{ fontSize: 11, color: "#888", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.userEmail}</div>}
                          </div>
                          <span style={{ flexShrink: 0, fontSize: 10, fontWeight: "bold", color: "#fff", backgroundColor: getStatusColor(m.status), borderRadius: 10, padding: "2px 8px" }}>
                            {shortStatus(m.status)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Einzelanfragen */}
            {ordered.map((req) => {
              const isFollowUp = req.linkedToId && byId[req.linkedToId] && filteredIds.has(byId[req.linkedToId].id);
              const parentReq = isFollowUp ? byId[req.linkedToId] : null;
              const isNew = !!newRequestIds[req.id];
              return (
              <div key={req.id}>
                {isFollowUp && (
                  <div style={{ display: "flex", alignItems: "center", marginLeft: 16, marginBottom: 2, marginTop: 2, gap: 6 }}>
                    <div style={{ width: 2, height: 16, backgroundColor: "#90caf9", borderRadius: 2, marginRight: 6, flexShrink: 0 }} />
                    <div style={{ fontSize: 11, color: "#1565c0", fontWeight: "bold", backgroundColor: "#e3f2fd", borderRadius: 10, padding: "2px 8px", border: "1px solid #90caf9", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "calc(100% - 30px)" }}>
                      ↳ {t.followUpOf} {parentReq?.finalPlan?.requirements?.from} → {parentReq?.finalPlan?.requirements?.to}
                    </div>
                  </div>
                )}
                <div
                  onClick={() => {
                    setSelectedRequest(req);
                    clearNewMarker(req.id);
                  }}
                  style={{ padding: "10px 12px", marginBottom: 8, marginLeft: isFollowUp ? 16 : 0, backgroundColor: selectedRequest?.id === req.id ? "#e3f2fd" : "#fff", borderRadius: 8, cursor: "pointer", border: isNew ? "2px solid #43a047" : selectedRequest?.id === req.id ? "2px solid #2196f3" : isFollowUp ? "1px solid #90caf9" : "1px solid #ddd", boxShadow: isNew ? "0 0 0 3px rgba(67,160,71,0.12)" : selectedRequest?.id === req.id ? "0 2px 8px rgba(33,150,243,0.3)" : "0 1px 3px rgba(0,0,0,0.1)", transition: "all 0.2s" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ fontSize: 14 }}>{req.userName}</strong>
                      {req.userEmail && <div style={{ fontSize: 11, color: "#666", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{req.userEmail}</div>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0 }}>
                      <div style={{ padding: "3px 9px", borderRadius: 12, backgroundColor: getStatusColor(req.status), color: "white", fontSize: 10, fontWeight: "bold" }}>
                        {shortStatus(req.status)}
                      </div>
                      {req.status !== "pending" && req.statusBy && (
                        <div style={{ fontSize: 10, color: "#9c27b0", fontWeight: "bold" }}>👤 {req.statusBy}</div>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "#777" }}>
                    📅 {new Date(req.checkin).toLocaleDateString()} - {req.checkout ? new Date(req.checkout).toLocaleDateString() : t.tripEndOpen}
                    {req.finalPlan?.requirements?.from && req.finalPlan?.requirements?.to && (
                      <span> · ✈️ {req.finalPlan.requirements.from} → {req.finalPlan.requirements.to}</span>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
            </>
            );
          })()}
        </div>

        {/* Right: request detail */}
        <div style={{ flex: 1, padding: 20, overflowY: "auto", backgroundColor: "#fff" }}>
          {!selectedRequest ? (
            <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontSize: 18 }}>
              {t.selectPrompt}
            </div>
          ) : (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, paddingBottom: 15, borderBottom: "2px solid #eee" }}>
                <div>
                  <h2 style={{ margin: 0 }}>{t.requestTitle}{selectedRequest.id}</h2>
                  {selectedRequest.linkedToId && requests.find(r => r.id === selectedRequest.linkedToId) && (() => {
                    const parent = requests.find(r => r.id === selectedRequest.linkedToId);
                    return (
                      <div
                        onClick={() => setSelectedRequest(parent)}
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 6, fontSize: 12, color: "#1565c0", fontWeight: "bold", backgroundColor: "#e3f2fd", borderRadius: 10, padding: "3px 10px", border: "1px solid #90caf9", cursor: "pointer" }}
                      >
                        ↳ {t.followUpOf} {parent.finalPlan?.requirements?.from} → {parent.finalPlan?.requirements?.to} ({parent.checkin})
                      </div>
                    );
                  })()}
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                    {selectedRequest.status === "draft" ? (
                      <div style={{ padding: "8px 12px", borderRadius: 4, backgroundColor: "#eceff1", border: "2px solid #607d8b", color: "#455a64", fontSize: 13, fontWeight: "bold" }}>
                        {t.statusDraft} — {t.draftAwaitingEmployee}
                      </div>
                    ) : (
                      <select
                        value={selectedRequest.status}
                        onChange={(e) => updateStatus(selectedRequest.id, e.target.value)}
                        style={{ padding: "8px 12px", borderRadius: 4, border: "2px solid #ddd", fontSize: 14, fontWeight: "bold", cursor: "pointer" }}
                      >
                        <option value="pending">{t.statusPending}</option>
                        <option value="in progress">{t.statusInProgress}</option>
                        <option value="confirmed">{t.statusConfirmed}</option>
                        <option value="cancelled">{t.statusCancelled}</option>
                      </select>
                    )}
                    {selectedRequest.status !== "pending" && selectedRequest.statusBy && (
                      <div style={{ fontSize: 11, color: "#9c27b0", fontWeight: "bold" }}>
                        👤 {selectedRequest.statusBy}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => deleteRequest(selectedRequest.id)}
                    style={{ padding: "8px 16px", backgroundColor: "#f44336", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: "bold" }}
                  >
                    {t.deleteBtn}
                  </button>
                </div>
              </div>

              {/* User info */}
              <div style={{ padding: 15, backgroundColor: "#f5f5f5", borderRadius: 8, marginBottom: 20 }}>
                <h3 style={{ marginTop: 0 }}>{t.userInfo}</h3>
                <div style={{ marginBottom: 8 }}><strong>{t.name}</strong> {selectedRequest.userName}</div>
                {selectedRequest.userEmail && (
                  <div style={{ marginBottom: 8 }}><strong>{t.email}</strong> {selectedRequest.userEmail}</div>
                )}
                {selectedRequest.plannedByName && (
                  <div style={{ marginBottom: 8, padding: "8px 10px", backgroundColor: "#eceff1", borderRadius: 6, borderLeft: "3px solid #607d8b" }}>
                    <div><strong>{t.plannedBy}</strong> {selectedRequest.plannedByName}{selectedRequest.plannedByEmail ? ` (${selectedRequest.plannedByEmail})` : ""}</div>
                    {siblings.length > 1 && (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ fontSize: 13, color: "#555", marginBottom: 4 }}>{t.groupRecipients}</div>
                        {siblings.map((s) => (
                          <div key={s.id} style={{ fontSize: 13, marginBottom: 2 }}>
                            • {s.userName}{s.userEmail ? ` (${s.userEmail})` : ""}{" "}
                            <span style={{ color: "#607d8b" }}>— {t[`reqStatus_${(s.status || "").replace(" ", "_")}`] || s.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ marginBottom: 8 }}>
                  <strong>{t.travelPeriod}</strong> {new Date(selectedRequest.checkin).toLocaleDateString()}
                  {selectedRequest.checkout
                    ? <> {t.periodTo} {new Date(selectedRequest.checkout).toLocaleDateString()}</>
                    : <> ({t.tripEndOpen})</>}
                </div>
                {(() => {
                  const acc = selectedRequest.finalPlan?.requirements?.accommodation;
                  return acc?.needed && acc?.checkIn ? (
                    <div style={{ marginBottom: 8 }}>
                      <strong>{t.hotelPeriod}</strong> {acc.checkIn}{acc.checkOut ? ` ${t.periodTo} ${acc.checkOut}` : ""}
                    </div>
                  ) : null;
                })()}
                {(() => {
                  const os = selectedRequest.finalPlan?.requirements?.onSite;
                  return os && (os.from || os.to) ? (
                    <div style={{ marginBottom: 8 }}>
                      <strong>{t.onSitePeriod}</strong> {os.from || "?"}{os.to ? ` ${t.periodTo} ${os.to}` : ""}
                    </div>
                  ) : null;
                })()}
                {selectedRequest.businessTripNumber && (
                  <div style={{ marginBottom: 8 }}><strong>{t.businessTripNumber}</strong> {selectedRequest.businessTripNumber}</div>
                )}
                <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <strong>{t.projectNumber}</strong>
                  {editingProject ? (
                    <>
                      <input
                        value={projectNumberDraft}
                        onChange={e => setProjectNumberDraft(e.target.value)}
                        style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid #ccc", fontSize: 14, width: 150 }}
                      />
                      <button
                        onClick={() => saveProjectNumber(selectedRequest.id)}
                        style={{ padding: "3px 12px", backgroundColor: "#4caf50", color: "#fff", border: "none", borderRadius: 4, fontSize: 13, cursor: "pointer", fontWeight: "bold" }}
                      >{t.projectNumberSave}</button>
                      <button
                        onClick={() => setEditingProject(false)}
                        style={{ padding: "3px 12px", backgroundColor: "#eee", color: "#555", border: "none", borderRadius: 4, fontSize: 13, cursor: "pointer" }}
                      >{t.projectNumberCancel}</button>
                    </>
                  ) : (
                    <>
                      <span>{selectedRequest.projectNumber || t.projectNumberNone}</span>
                      <button
                        onClick={() => { setEditingProject(true); setProjectNumberDraft(selectedRequest.projectNumber || ""); }}
                        style={{ padding: "3px 12px", backgroundColor: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9", borderRadius: 4, fontSize: 13, cursor: "pointer", fontWeight: "bold" }}
                      >{t.projectNumberEdit}</button>
                    </>
                  )}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                    <strong>{t.adminInfoTitle}</strong>
                    {!editingInfo && (
                      <button
                        onClick={() => { setEditingInfo(true); setInfoDraft(selectedRequest.adminInfo || ""); }}
                        style={{ padding: "3px 12px", backgroundColor: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9", borderRadius: 4, fontSize: 13, cursor: "pointer", fontWeight: "bold" }}
                      >{t.adminInfoEdit}</button>
                    )}
                  </div>
                  {editingInfo ? (
                    <>
                      <textarea
                        value={infoDraft}
                        onChange={e => setInfoDraft(e.target.value)}
                        placeholder={t.adminInfoPlaceholder}
                        rows={5}
                        style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 4, border: "1px solid #ccc", fontSize: 14, fontFamily: "inherit", resize: "vertical" }}
                      />
                      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                        <button
                          onClick={() => saveAdminInfo(selectedRequest.id)}
                          style={{ padding: "4px 14px", backgroundColor: "#4caf50", color: "#fff", border: "none", borderRadius: 4, fontSize: 13, cursor: "pointer", fontWeight: "bold" }}
                        >{t.adminInfoSave}</button>
                        <button
                          onClick={() => setEditingInfo(false)}
                          style={{ padding: "4px 14px", backgroundColor: "#eee", color: "#555", border: "none", borderRadius: 4, fontSize: 13, cursor: "pointer" }}
                        >{t.adminInfoCancel}</button>
                      </div>
                    </>
                  ) : (
                    <div style={{ whiteSpace: "pre-wrap", fontSize: 14, color: selectedRequest.adminInfo ? "#333" : "#999", backgroundColor: "#fafafa", border: "1px solid #eee", borderRadius: 6, padding: "8px 10px", minHeight: 20 }}>
                      {selectedRequest.adminInfo || t.adminInfoEmpty}
                    </div>
                  )}
                </div>
                <div><strong>{t.created}</strong> {new Date(selectedRequest.timestamp).toLocaleString()}</div>
              </div>

              {/* Final travel plan */}
              {selectedRequest.finalPlan && (
                <div style={{ border: "2px solid #4caf50", padding: 15, borderRadius: 8, marginBottom: 20 }}>
                  <h3 style={{ color: "#4caf50", marginTop: 0 }}>{t.travelPlan}</h3>
                  {selectedRequest.finalPlan.summary && (
                    <p style={{ fontStyle: "italic", marginBottom: 15, backgroundColor: "#f1f8e9", padding: 12, borderRadius: 4, borderLeft: "4px solid #4caf50" }}>
                      {selectedRequest.finalPlan.summary}
                    </p>
                  )}
                  {selectedRequest.finalPlan.flights && selectedRequest.finalPlan.flights.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <h4 style={{ marginBottom: 10 }}>{t.flights}</h4>
                      {selectedRequest.finalPlan.flights.map((f, i) => (
                        <div key={i} style={{ marginBottom: 10, padding: 12, backgroundColor: "#e3f2fd", borderRadius: 5, borderLeft: "4px solid #2196f3" }}>
                          <div style={{ marginBottom: 5 }}><strong>{t.route}</strong> {f.from} → {f.to}</div>
                          <div style={{ marginBottom: 5 }}><strong>{t.date}</strong> {f.date}</div>
                          <div style={{ marginBottom: 5 }}><strong>{t.airlines}</strong> {f.airlines?.join(", ")}</div>
                          <div style={{ marginBottom: 5 }}><strong>{t.price}</strong> {f.priceRange} €</div>
                          {f.luggage && <div style={{ marginBottom: 5 }}><strong>{t.luggage}</strong> {f.luggage}</div>}
                          {f.flightLink && <a href={f.flightLink} target="_blank" rel="noopener noreferrer" style={{ color: "#2196f3", fontWeight: "bold", textDecoration: "none" }}>{t.openFlights}</a>}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedRequest.finalPlan.hotels && selectedRequest.finalPlan.hotels.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <h4 style={{ marginBottom: 10 }}>{t.hotelsTitle} ({selectedRequest.finalPlan.hotels.length} {t.hotelsOptions})</h4>
                      {selectedRequest.finalPlan.hotels.map((h, i) => (
                        <div key={i} style={{ marginBottom: 10, padding: 12, backgroundColor: "#fff3e0", borderRadius: 5, borderLeft: "4px solid #ff9800" }}>
                          <div style={{ marginBottom: 5 }}><strong>{h.name}</strong> ({h.area})</div>
                          {h.stars && <div style={{ marginBottom: 5 }}>⭐ {h.stars} {t.stars}</div>}
                          <div style={{ marginBottom: 5 }}><strong>{t.price}</strong> {h.priceRange} € / Nacht</div>
                          <div style={{ marginBottom: 5 }}><strong>{t.rating}</strong> {h.rating}</div>
                          {h.bookingLink && <a href={h.bookingLink} target="_blank" rel="noopener noreferrer" style={{ color: "#ff9800", fontWeight: "bold", textDecoration: "none" }}>{t.openBooking}</a>}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedRequest.finalPlan.carRental?.needed && (
                    <div>
                      <h4>{t.carRental}</h4>
                      <p style={{ padding: 10, backgroundColor: "#e8f5e9", borderRadius: 4, borderLeft: "4px solid #4caf50" }}>{t.carRentalNeeded}</p>
                    </div>
                  )}
                </div>
              )}

              {/* User notes */}
              {selectedRequest.finalPlan?.userNotes && (
                <div style={{ marginBottom: 20, padding: 15, backgroundColor: "#fff9c4", borderRadius: 8, border: "2px solid #fbc02d" }}>
                  <h3 style={{ marginTop: 0, color: "#f57f17" }}>{t.userComment}</h3>
                  <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 14, lineHeight: 1.6, padding: 10, backgroundColor: "#fff", borderRadius: 4, border: "1px solid #ddd" }}>
                    {selectedRequest.finalPlan.userNotes}
                  </div>
                </div>
              )}

              {/* Conversation */}
              {selectedRequest.conversation && selectedRequest.conversation.length > 0 && (
                <div>
                  <h3>{t.conversation}</h3>
                  <div style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #ddd", borderRadius: 8, padding: 10 }}>
                    {selectedRequest.conversation.map((msg, i) => (
                      <div key={i} style={{ padding: 10, marginBottom: 10, backgroundColor: msg.role === "user" ? "#e3f2fd" : "#f5f5f5", borderRadius: 8, borderLeft: `4px solid ${msg.role === "user" ? "#2196f3" : "#ff9800"}` }}>
                        <strong>{msg.role === "user" ? t.userLabel : t.aiLabel}</strong>
                        <div style={{ marginTop: 5 }}>
                          {msg.text && <p style={{ margin: 0 }}>{msg.text}</p>}
                          {msg.question && <p style={{ fontWeight: "bold", color: "#ff9800", margin: 0 }}>❓ {msg.question}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Messages */}
              <div style={{ marginTop: 24 }}>
                <h3 id="admin-messages" style={{ marginBottom: 10 }}>{t.messagesTitle}</h3>
                <div style={{ maxHeight: 250, overflowY: "auto", border: "1px solid #ddd", borderRadius: 8, padding: 10, marginBottom: 10, backgroundColor: "#fafafa" }}>
                  {adminMessages.length === 0 ? (
                    <div style={{ color: "#999", textAlign: "center", padding: 10 }}>{t.noMessages}</div>
                  ) : (
                    adminMessages.map(m => (
                      <div key={m.id} style={{ padding: 10, marginBottom: 8, borderRadius: 6, backgroundColor: m.sender === "admin" ? "#fff3e0" : "#e3f2fd", borderLeft: `4px solid ${m.sender === "admin" ? "#ff9800" : "#2196f3"}` }}>
                        <div style={{ fontSize: 11, color: "#999", marginBottom: 4 }}>
                          {m.senderName || (m.sender === "admin" ? t.msgAdmin : t.msgUser)} · {new Date(m.timestamp).toLocaleString()}
                        </div>
                        {m.text && <div style={{ fontSize: 14 }}>{m.text}</div>}
                        {m.images?.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: m.text ? 6 : 0 }}>
                            {m.images.map((src, i) => (
                              <img key={i} src={src} alt="" onClick={() => setLightboxSrc(src)} style={{ maxHeight: 180, maxWidth: "100%", borderRadius: 6, border: "1px solid #ddd", cursor: "pointer", display: "block" }} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
                {adminMsgImages.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                    {adminMsgImages.map((src, i) => (
                      <div key={i} style={{ position: "relative" }}>
                        <img src={src} alt="" style={{ height: 64, width: 64, objectFit: "cover", borderRadius: 6, border: "1px solid #ddd" }} />
                        <button
                          onClick={() => setAdminMsgImages(prev => prev.filter((_, j) => j !== i))}
                          title={t.removeImage}
                          style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, lineHeight: "16px", padding: 0, borderRadius: "50%", border: "none", backgroundColor: "#c62828", color: "#fff", fontSize: 12, cursor: "pointer" }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <label
                    title={t.attachImage}
                    style={{ padding: "8px 12px", backgroundColor: "#fff3e0", color: "#e65100", border: "1px solid #ffcc80", borderRadius: 4, cursor: adminImgBusy ? "wait" : "pointer", fontSize: 18, display: "flex", alignItems: "center" }}
                  >
                    {adminImgBusy ? "⏳" : "📎"}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      disabled={adminImgBusy}
                      style={{ display: "none" }}
                      onChange={e => { addAdminImageFiles(e.target.files); e.target.value = ""; }}
                    />
                  </label>
                  <input
                    style={{ flex: 1, padding: "8px 12px", borderRadius: 4, border: "1px solid #ccc", fontSize: 14 }}
                    value={adminMsgInput}
                    onChange={e => setAdminMsgInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && sendAdminMessage()}
                    onPaste={handleAdminPaste}
                    placeholder={t.msgPlaceholder}
                  />
                  <button
                    onClick={sendAdminMessage}
                    style={{ padding: "8px 16px", backgroundColor: "#ff9800", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: "bold", fontSize: 14 }}
                  >
                    {t.send}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
        )}
      </div>

      {/* Bild-Lightbox: großes Bild als In-App-Dialog, responsiv (auch auf dem Handy) */}
      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          style={{ position: "fixed", inset: 0, zIndex: 1000, backgroundColor: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
          <button
            onClick={() => setLightboxSrc(null)}
            aria-label={t.close || "×"}
            style={{ position: "absolute", top: 12, right: 16, width: 40, height: 40, borderRadius: "50%", border: "none", backgroundColor: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 24, lineHeight: "40px", cursor: "pointer" }}
          >
            ×
          </button>
          <img
            src={lightboxSrc}
            alt=""
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 6, boxShadow: "0 4px 24px rgba(0,0,0,0.5)" }}
          />
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
